/**
 * Bridge between the slim All-e Recommender form state and the unchanged
 * 8-check engine in functions/_lib/alle-check.
 *
 * The Recommender only collects inputs that drive viability and the Solo/Duo
 * recommendation (Checks 0-4). For the engine's install + electrical inputs
 * (Checks 5/6/7) we send fixed neutral defaults so those checks always return
 * status="geschikt" and don't leak into the displayed verdict.
 *
 * When the engine is later refactored to make Checks 5/6/7 inputs optional,
 * delete this helper and have the form call the engine with the slim shape
 * directly.
 */

import type { AllEInput, AllEOutput, CheckStatus } from "../../../functions/_lib/alle-check/types";

/** Form state of the All-e Recommender. the only fields the user actually fills in. */
export interface RecommenderFormState {
  // Section 1: woningtype + uitsluiting
  house_type: string;
  apartment_vve: boolean | null;
  apartment_outdoor_space: boolean | null;
  heating_types: string[];
  is_renovating: boolean;

  // Section 2: route
  route: "gas" | "beng1";
  yearly_gas_m3: string;
  summer_avg_gas_m3: string;
  beng1_value: string;
  floor_area_m2: string;

  // Section 3: isolatie
  build_year: string;
  energy_label: string;
  insulation_measures: string[];

  // Section 4: verwarmingssysteem & temperatuur
  cv_comfort_55c: boolean;
  /** Toggle for silent-mode-at-night sizing path. Default false (no silent). */
  silent_mode_at_night: boolean;
  distribution_main_room: string;
  distribution_whole_house: string;
  day_temp: string;
  night_temp: string;

  // Section 4 (HTML parity): thermostat flags
  /** Zone-thermostaat aanwezig (HTML red flag when "ja"). */
  zone_thermostat: "ja" | "nee" | "";
  /** Extra warmtebronnen aanwezig (HTML amber flag when "ja"). */
  extra_heat_sources: "ja" | "nee" | "";
  /** Tapwaterklasse (HTML amber flag when "CW6"). */
  cw_class: "CW5" | "CW6" | "";

  // Section 5: radiator capacity (optional; only shown when high CV + radiators)
  /** Numeric CV supply temperature in degrees C (form string). */
  cv_temp_c: string;
  /** Manifold pump present (only relevant for floor heating). */
  manifold_pump: boolean | null;
  /** Per-radiator dimensions. */
  radiators: Array<{
    type: "10" | "11" | "20" | "21" | "22" | "33" | "";
    length_cm: string;
    height_cm: string;
    room: string;
  }>;
  /** Coverage percentage (default "70"). */
  radiator_coverage_pct: string;

  // Optional accuracy block
  resident_number: string;
  boiler_age: string;
  hot_water_usage: string;
  estimated_electricity_usage: string;
  insulation_score_roof: string;
  insulation_score_wall: string;
  insulation_score_floor: string;
  insulation_score_windows: string;

  // Optional address (cosmetic)
  postal_code: string;
  house_number: string;
  house_number_addition: string;
  address_street: string;
  address_city: string;
}

/** Neutral defaults for the install/electrical fields that the Recommender does not collect. */
const PARKED_INSTALL_DEFAULTS = {
  heatbattery_space: "ja",
  outdoor_unit_placement: "begane_grond",
  piping_distance: "0_10",
  wall_material: "baksteen",
  free_electrical_group: "ja",
  panel_replacement_needed: "nee",
} as const;

/** Map the slim form state to a full AllEInput the engine accepts. */
export function buildRecommenderInput(s: RecommenderFormState): AllEInput {
  const insulationScores: Record<string, number> = {};
  const addScore = (k: string, raw: string) => {
    const n = Number(raw);
    if (raw !== "" && Number.isFinite(n)) insulationScores[k] = n;
  };
  addScore("roof", s.insulation_score_roof);
  addScore("wall", s.insulation_score_wall);
  addScore("floor", s.insulation_score_floor);
  addScore("windows", s.insulation_score_windows);

  const isApartment = s.house_type === "Appartement";

  // Radiator capacity inputs. only forwarded when at least one row has a type
  // + numeric dimensions. Keeps the engine input clean when the section is
  // hidden or unfilled.
  const radiatorsClean = (s.radiators ?? [])
    .map((r) => ({
      type: r.type,
      length_cm: Number(r.length_cm),
      height_cm: Number(r.height_cm),
      room: r.room || undefined,
    }))
    .filter(
      (r) =>
        r.type !== "" &&
        Number.isFinite(r.length_cm) &&
        r.length_cm > 0 &&
        Number.isFinite(r.height_cm) &&
        r.height_cm > 0,
    ) as Array<{ type: "10" | "11" | "20" | "21" | "22" | "33"; length_cm: number; height_cm: number; room?: string }>;

  const cvTempNum = s.cv_temp_c ? Number(s.cv_temp_c) : undefined;
  const coverageNum = s.radiator_coverage_pct ? Number(s.radiator_coverage_pct) : undefined;

  const base = {
    house_type: s.house_type,
    heating_types: s.heating_types,
    is_renovating: s.is_renovating,
    cv_comfort_55c: s.cv_comfort_55c,
    silent_mode_at_night: s.silent_mode_at_night,
    distribution_main_room: s.distribution_main_room,
    distribution_whole_house: s.distribution_whole_house,
    day_temp: Number(s.day_temp),
    night_temp: Number(s.night_temp),
    build_year: s.build_year || undefined,
    energy_label: s.energy_label || undefined,
    insulation_measures: s.insulation_measures,
    ...PARKED_INSTALL_DEFAULTS,
    ...(isApartment
      ? { apartment_vve: s.apartment_vve, apartment_outdoor_space: s.apartment_outdoor_space }
      : {}),
    resident_number: s.resident_number ? Number(s.resident_number) : undefined,
    boiler_age: (s.boiler_age || undefined) as AllEInput["boiler_age"],
    hot_water_usage: (s.hot_water_usage || undefined) as AllEInput["hot_water_usage"],
    estimated_electricity_usage: s.estimated_electricity_usage
      ? Number(s.estimated_electricity_usage)
      : undefined,
    insulation_scores: Object.keys(insulationScores).length > 0 ? insulationScores : undefined,
    // Floor area (m2) -- usually auto-filled from BAG via the address lookup.
    // Feeds the savings model's `dimensions` so it can recommend Single vs Duo.
    floor_area_m2_opt:
      s.floor_area_m2 && Number.isFinite(Number(s.floor_area_m2)) ? Number(s.floor_area_m2) : undefined,
    postal_code: s.postal_code || undefined,
    house_number: s.house_number || undefined,
    house_number_addition: s.house_number_addition || undefined,
    // Radiator capacity inputs (optional)
    cv_temp_c: cvTempNum !== undefined && Number.isFinite(cvTempNum) ? cvTempNum : undefined,
    manifold_pump: s.manifold_pump ?? undefined,
    radiators: radiatorsClean.length > 0 ? radiatorsClean : undefined,
    radiator_coverage_pct:
      coverageNum !== undefined && Number.isFinite(coverageNum) ? coverageNum : undefined,
    // Thermostat flags (HTML Step 3 parity)
    zone_thermostat: s.zone_thermostat ? s.zone_thermostat : undefined,
    extra_heat_sources: s.extra_heat_sources ? s.extra_heat_sources : undefined,
    cw_class: s.cw_class ? s.cw_class : undefined,
  };

  if (s.route === "gas") {
    return {
      ...base,
      route: "gas",
      yearly_gas_m3: Number(s.yearly_gas_m3),
      summer_avg_gas_m3: Number(s.summer_avg_gas_m3),
    } as AllEInput;
  }
  return {
    ...base,
    route: "beng1",
    beng1_value: Number(s.beng1_value),
    floor_area_m2: Number(s.floor_area_m2),
  } as AllEInput;
}

/**
 * Combine the recommender-relevant checks (0/1/2/3/4) into a single verdict.
 *
 * The engine's `result.overall` folds in Checks 5/6/7 too. since the
 * Recommender form sends neutral defaults that always score "geschikt" for
 * those, `result.overall` happens to match the combined verdict in practice,
 * but recomputing here keeps the Recommender's surface honest about *which*
 * checks it actually exercised.
 */
export function combineRecommenderVerdict(result: AllEOutput): CheckStatus {
  const statuses: CheckStatus[] = [
    result.check0_exclusions.status,
    result.check1_heat_demand.status,
    result.check2_water_temp.status,
    result.check3_thermostat.status,
    result.check4_insulation.status,
  ];
  if (statuses.includes("ongeschikt")) return "ongeschikt";
  if (statuses.includes("extra_controle")) return "extra_controle";
  return "geschikt";
}

/**
 * Lighter "3-check" verdict used by the unified tool's headline: exclusions +
 * warmtevraag + watertemperatuur + thermostaatgedrag. Drops the advisory
 * insulation check (Check 4) from the headline verdict; it stays visible in the
 * per-check grid. Mirrors the same helper in the check endpoint.
 */
export function combineThreeCheckVerdict(result: AllEOutput): CheckStatus {
  const statuses: CheckStatus[] = [
    result.check0_exclusions.status,
    result.check1_heat_demand.status,
    result.check2_water_temp.status,
    result.check3_thermostat.status,
  ];
  if (statuses.includes("ongeschikt")) return "ongeschikt";
  if (statuses.includes("extra_controle")) return "extra_controle";
  return "geschikt";
}
